import { supabase } from '../client';
import type { Experience } from '@/types';

interface CreateExperienceData {
  title: string;
  description: string;
  shortDescription?: string;
  price: number;
  duration: number;
  location: {
    name: string;
    address: string;
    city: string;
    country: string;
    coordinates?: {
      latitude: number;
      longitude: number;
    };
  };
  category: string;
  media: Array<{
    url: string;
    type: 'image' | 'video';
    order_index: number;
    metadata?: Record<string, any>;
  }>;
  bookingType: 'instant' | 'request';
  requireApproval?: boolean;
  requirementsDescription?: string;
  cancellationPolicy: 'flexible' | 'moderate' | 'strict';
}

class ExperienceService {
  private async cleanupMedia(urls: string[]) {
    try {
      // Extract paths from URLs
      const paths = urls.map(url => {
        const path = new URL(url).pathname;
        return path.startsWith('/storage/v1/object/public/') 
          ? path.replace('/storage/v1/object/public/', '')
          : path;
      });

      // Delete files from storage
      const { error } = await supabase.storage
        .from('experience-media')
        .remove(paths);

      if (error) {
        console.error('Failed to cleanup media:', error);
      }
    } catch (error) {
      console.error('Error during media cleanup:', error);
    }
  }

  async createExperience(creatorId: string, data: CreateExperienceData): Promise<Experience> {
    const uploadedMediaUrls: string[] = [];

    try {
      console.log('Creating experience:', { creatorId, data });

      // Check for existing experience with same title
      const { data: existing } = await supabase
        .from('experiences')
        .select('id')
        .eq('creator_id', creatorId)
        .eq('title', data.title)
        .maybeSingle();

      if (existing) {
        throw new Error('An experience with this title already exists');
      }

      // First get the category ID
      const { data: category, error: categoryError } = await supabase
        .from('categories')
        .select('id')
        .eq('name', data.category)
        .single();

      if (categoryError) {
        console.error('Category error:', categoryError);
        throw categoryError;
      }

      // Track uploaded media URLs for potential cleanup
      uploadedMediaUrls.push(...data.media.map(m => m.url));

      console.log('Starting experience creation with media:', data.media);

      // Start a transaction by using RPC
      const { data: experience, error: experienceError } = await supabase.rpc(
        'create_experience_with_media',
        {
          creator_id_param: creatorId,
          experience_data: {
            title: data.title,
            description: data.description,
            short_description: data.shortDescription,
            price: data.price,
            duration: data.duration,
            category_id: category.id,
            location: data.location,
            booking_type: data.bookingType,
            approval_required: data.requireApproval,
            requirements_description: data.requirementsDescription,
            cancellation_policy: data.cancellationPolicy,
            status: 'pending'
          },
          media_data: data.media.map(media => ({
            url: media.url,
            type: media.type,
            order_index: media.order_index,
            metadata: media.metadata || {}
          }))
        }
      );

      if (experienceError) {
        console.error('Experience creation error:', experienceError);
        // If transaction fails, cleanup uploaded media
        await this.cleanupMedia(uploadedMediaUrls);
        throw experienceError;
      }

      console.log('Experience created successfully:', experience);
      return experience;
    } catch (error) {
      console.error('Error creating experience:', error);
      // Cleanup media on any error
      await this.cleanupMedia(uploadedMediaUrls);
      throw error;
    }
  }

  async update(id: string, data: Partial<CreateExperienceData>): Promise<Experience> {
    const uploadedMediaUrls: string[] = [];

    try {
      // Get category ID if category is being updated
      let categoryId;
      if (data.category) {
        const { data: category, error: categoryError } = await supabase
          .from('categories')
          .select('id')
          .eq('name', data.category)
          .single();

        if (categoryError) throw categoryError;
        categoryId = category.id;
      }

      // Track any new media being uploaded
      if (data.media) {
        uploadedMediaUrls.push(...data.media.map(m => m.url));
      }

      // Start transaction using RPC
      const { data: experience, error: updateError } = await supabase.rpc(
        'update_experience_with_media',
        {
          experience_id_param: id,
          experience_data: {
            title: data.title,
            description: data.description,
            short_description: data.shortDescription,
            price: data.price,
            duration: data.duration,
            category_id: categoryId,
            location: data.location,
            booking_type: data.bookingType,
            approval_required: data.requireApproval,
            requirements_description: data.requirementsDescription,
            cancellation_policy: data.cancellationPolicy
          },
          media_data: data.media?.map(media => ({
            url: media.url,
            type: media.type,
            order_index: media.order_index,
            metadata: media.metadata || {}
          }))
        }
      );

      if (updateError) {
        console.error('Experience update error:', updateError);
        // If transaction fails, cleanup any newly uploaded media
        await this.cleanupMedia(uploadedMediaUrls);
        throw updateError;
      }

      return experience;
    } catch (error) {
      console.error('Error updating experience:', error);
      // Cleanup media on any error
      await this.cleanupMedia(uploadedMediaUrls);
      throw error;
    }
  }

  async delete(id: string): Promise<void> {
    try {
      // Get media URLs first
      const { data: media } = await supabase
        .from('experience_media')
        .select('url')
        .eq('experience_id', id);

      // Delete the experience (this will cascade to experience_media)
      const { error } = await supabase
        .from('experiences')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // Cleanup media files
      if (media && media.length > 0) {
        await this.cleanupMedia(media.map(m => m.url));
      }
    } catch (error) {
      console.error('Error deleting experience:', error);
      throw error;
    }
  }
}

// Create and export a singleton instance
export const experienceService = new ExperienceService();